/** Piezas comunes del admin (solo español, decisión 13/10: el admin no se traduce). */
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { tokens } from '@nuvvu/ui';

export const card: CSSProperties = {
  background: tokens.color.card,
  border: `1px solid ${tokens.color.border}`,
  borderRadius: tokens.radius.xl,
  padding: 16,
  display: 'grid',
  gap: 10,
};
export const field: CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '8px 10px',
  borderRadius: tokens.radius.lg,
  border: `1px solid ${tokens.color.border}`,
  background: tokens.color.card,
  color: tokens.color.fg,
  fontSize: 14,
  fontFamily: 'inherit',
};
export const btn = (primary = false, danger = false): CSSProperties => ({
  padding: '8px 14px',
  borderRadius: tokens.radius.pill,
  border: primary || danger ? 'none' : `1px solid ${tokens.color.border}`,
  background: danger ? tokens.color.danger : primary ? tokens.color.primary : tokens.color.card,
  color: primary || danger ? '#fff' : tokens.color.fg,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
});
export const muted: CSSProperties = { color: tokens.color.muted, fontSize: 13 };

export function Table({ head, children }: { head: string[]; children: ReactNode }): ReactElement {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {head.map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '8px 6px',
                  borderBottom: `1px solid ${tokens.color.border}`,
                  color: tokens.color.muted,
                  fontWeight: 600,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
export const td: CSSProperties = {
  padding: '8px 6px',
  borderBottom: `1px solid ${tokens.color.border}`,
  verticalAlign: 'top',
};

export function Notice({
  text,
  error,
}: {
  text: string | null;
  error?: boolean;
}): ReactElement | null {
  if (!text) return null;
  return (
    <p
      style={{
        margin: 0,
        padding: 10,
        borderRadius: tokens.radius.lg,
        background: error ? '#FEE2E2' : tokens.color.border,
        color: error ? '#991B1B' : tokens.color.fg,
        fontSize: 13,
      }}
    >
      {text}
    </p>
  );
}

export function when(v: unknown): string {
  const d =
    v && typeof v === 'object' && 'toDate' in v
      ? (v as { toDate: () => Date }).toDate()
      : typeof v === 'string' || typeof v === 'number'
        ? new Date(v)
        : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString('es-ES') : '';
}

export function money(m: unknown): string {
  if (!m || typeof m !== 'object') return '';
  const { amount, currency } = m as { amount?: number; currency?: string };
  if (typeof amount !== 'number' || !currency) return '';
  const digits = ['HUF', 'ISK'].includes(currency) ? 0 : 2;
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(
      amount / 10 ** digits,
    );
  } catch {
    return `${amount / 10 ** digits} ${currency}`;
  }
}

/** Editor JSON con validación al guardar (ajustes, transportistas). */
export function JsonEditor({
  value,
  onChange,
  rows = 24,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}): ReactElement {
  return (
    <textarea
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      rows={rows}
      spellCheck={false}
      style={{
        ...field,
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.4,
      }}
    />
  );
}

/** Texto seguro de un valor desconocido (nunca «[object Object]»). */
export function str(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object' && 'toDate' in v) return when(v);
  return JSON.stringify(v);
}

/** Rango de fechas del admin (27/10): Hoy / 7 / 30 / 90 días / desde el principio / personalizado. */
export interface Range {
  from: number;
  to: number;
  label: string;
}
const DAY = 86_400_000;
export const rangeOf = (days: number | 'all'): Range => {
  const to = Date.now();
  if (days === 'all') return { from: 0, to, label: 'Desde el principio' };
  return {
    from: days === 1 ? new Date(new Date().toDateString()).getTime() : to - days * DAY,
    to,
    label: days === 1 ? 'Hoy' : `Últimos ${String(days)} días`,
  };
};
export function RangePicker({
  value,
  onChange,
}: {
  value: Range;
  onChange: (r: Range) => void;
}): ReactElement {
  const iso = (ms: number): string => new Date(ms || Date.now()).toISOString().slice(0, 10);
  const presets: (number | 'all')[] = [1, 7, 30, 90, 365, 'all'];
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map((p) => {
        const r = rangeOf(p);
        return (
          <button
            key={String(p)}
            type="button"
            style={btn(value.label === r.label)}
            onClick={() => {
              onChange(rangeOf(p));
            }}
          >
            {p === 'all' ? 'Desde el principio' : p === 1 ? 'Hoy' : `${String(p)} días`}
          </button>
        );
      })}
      <input
        type="date"
        style={{ ...field, width: 'auto' }}
        value={iso(value.from)}
        onChange={(e) => {
          const from = new Date(e.target.value).getTime();
          if (Number.isFinite(from)) onChange({ from, to: value.to, label: 'Personalizado' });
        }}
      />
      <span style={muted}>a</span>
      <input
        type="date"
        style={{ ...field, width: 'auto' }}
        value={iso(value.to)}
        onChange={(e) => {
          const to = new Date(e.target.value).getTime() + DAY - 1;
          if (Number.isFinite(to)) onChange({ from: value.from, to, label: 'Personalizado' });
        }}
      />
    </div>
  );
}
